import React from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { Flex, TextV2 } from '@withjoy/joykit';
import { NavLink } from '@react-router';
import { isSupportedEventPage } from '../../layouts/layout.utils';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { label4ToEventTitleOverride, label6ToNavigationOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useColorPaletteProvider } from '../../layouts/ColorPaletteProvider/ColorPaletteProvider';
import { pxToRem } from '@withjoy/joykit/theme';

type FooterNavProps = Readonly<{
  pages: ReadonlyArray<EventPageFragment>;
  eventHandle: string;
  eventDisplayName: Maybe<string>;
  handleMenuClicked?: (item: string) => void;
}>;

const FooterNav: React.FC<FooterNavProps> = ({ pages, eventHandle, eventDisplayName, handleMenuClicked }) => {
  const eventPageRoute = useEventPageRoute(eventHandle, pages);
  const { isDefaultPage } = useDefaultPage(pages);

  const { accentColor, pageFontColor } = useColorPaletteProvider();

  return (
    <Flex
      width="100%"
      flexDirection="column"
      rowGap={4}
      paddingX={7}
      paddingTop={pxToRem(96)}
      paddingBottom={8}
    >
      {eventDisplayName && (
        <GuestSiteTypographyOverride override={label4ToEventTitleOverride}>
          <TextV2
            typographyVariant="label4"
            fontWeight={500}
            textTransform="none"
            color={pageFontColor || 'mono8'}
          >
            {eventDisplayName}
          </TextV2>
        </GuestSiteTypographyOverride>
      )}
      <GuestSiteTypographyOverride override={label6ToNavigationOverride}>
        {pages.map(page => {
          if (!isSupportedEventPage(page.type) || !page.pageTitle) {
            return null;
          }

          return (
            <NavLink
              key={page.id}
              to={eventPageRoute.goToPath(page.type, page.pageSlug)}
              onClick={() => handleMenuClicked?.(page.pageSlug)}
              exact={isDefaultPage(page.pageSlug, page.type)}
              style={{ textDecoration: 'none' }}
            >
              <TextV2
                typographyVariant="label6"
                fontSize="19px"
                color={accentColor || 'mono15'}
              >
                {page.pageTitle}
              </TextV2>
            </NavLink>
          );
        })}
      </GuestSiteTypographyOverride>
    </Flex>
  );
};

export default FooterNav;
